import type { PostHogConfig } from 'posthog-js';

import { isPostHogCaptureEnabled } from '@eudia/utils/envs/is-posthog-capture-enabled';

import { shouldDropPostHogException } from './posthog-exception-filter';

type PostHogCaptureOptions = {
  captureEnabled?: boolean;
};

/**
 * Shared PostHog init options applied both at early-init time
 * (`instrumentation-client.ts` via `initPostHog`) and at provider-init time
 * (`PostHogAdapter`).
 *
 * Because `posthog-js` no-ops on a second `posthog.init()` call for the same
 * token, only the first caller's options take effect. Centralising them here
 * ensures consistent filtering regardless of which path wins.
 */
export function getSharedPostHogOptions({
  captureEnabled = isPostHogCaptureEnabled,
}: PostHogCaptureOptions = {}): Partial<PostHogConfig> {
  return {
    before_send: (event) => {
      if (!captureEnabled) return null;
      if (shouldDropPostHogException(event)) return null;
      return event;
    },
    ...(captureEnabled
      ? {}
      : {
          opt_out_capturing_by_default: true,
          autocapture: false,
          capture_pageview: false,
          capture_pageleave: false,
          capture_performance: false,
          disable_session_recording: true,
        }),
  };
}

/** @deprecated Use getSharedPostHogOptions() so capture mode stays in sync. */
export const SHARED_POSTHOG_OPTIONS = getSharedPostHogOptions();
